// app/javascript/controllers/pwa_controller.js
import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["installBlock", "installedBlock", "mobileBlock"]
  static values = { storageKey: { type: String, default: "oe2:pwa:dismissed" } }

  connect() {
    // Respect prior dismissal
    if (this._isDismissed()) {
      this._hideIfPresent(this.mobileBlockTarget)
      this._hideIfPresent(this.installBlockTarget)
      return
    }

    // If already installed / running standalone, show installed UI and remove banner
    if (this._isStandalone()) {
      this._showInstalledBlock()
      this._removeIfPresent(this.mobileBlockTarget)
      this._hideIfPresent(this.installBlockTarget)
      return
    }

    // Chromium prompt (Android, Windows, macOS Chrome/Edge)
    this._handleBeforeInstallPrompt()

    // When installation completes (Chromium)
    window.addEventListener("appinstalled", () => {
      this._showInstalledBlock()
      this._removeIfPresent(this.mobileBlockTarget)
      this._hideIfPresent(this.installBlockTarget)
      this._setDismissed(true)
    })
  }

  // Triggered by your CTA button
  install(event) {
    event.preventDefault()

    if (this._isStandalone()) {
      alert("The app is already installed on your device.")
      return
    }

    // Chromium path
    if (this.deferredPrompt) {
      this.deferredPrompt.prompt()
      this.deferredPrompt.userChoice.finally(() => {
        this.deferredPrompt = null
      })
      return
    }

    // Fallback instructions for platforms without beforeinstallprompt (iOS & Safari)
    this._showInstallInstructions()
  }

  // Hook this to your X button on the banner
  dismiss() {
    this._hideIfPresent(this.mobileBlockTarget)
    this._hideIfPresent(this.installBlockTarget)
    this._setDismissed(true)
  }

  // ---------- Internal helpers ----------

  _handleBeforeInstallPrompt() {
    window.addEventListener("beforeinstallprompt", (e) => {
      // iOS/Safari do NOT fire this; Chromium does
      e.preventDefault()
      this.deferredPrompt = e
      this._showIfPresent(this.installBlockTarget)
      this._showIfPresent(this.mobileBlockTarget)
    }, { once: true })

    // If no prompt event will ever fire (Safari/iOS), still show CTA so users can see instructions
    // Only do this when not standalone and not previously dismissed.
    setTimeout(() => {
      if (!this.deferredPrompt && !this._isStandalone() && !this._isDismissed()) {
        this._showIfPresent(this.installBlockTarget)
        this._showIfPresent(this.mobileBlockTarget)
      }
    }, 300)
  }

  _showInstalledBlock() {
    this._hideIfPresent(this.installBlockTarget)
    this._showIfPresent(this.installedBlockTarget)
  }

  _isStandalone() {
    // Chromium / PWA-capable browsers (Android, Windows, macOS)
    const displayModeStandalone = window.matchMedia && window.matchMedia("(display-mode: standalone)").matches
    // iOS Safari when launched from Home Screen
    const iosStandalone = typeof window.navigator.standalone === "boolean" && window.navigator.standalone === true
    // Some Android launch referrers may include android-app://
    const androidReferrer = document.referrer && document.referrer.startsWith("android-app://")
    return displayModeStandalone || iosStandalone || androidReferrer
  }

  _isIOS() {
    return /iPad|iPhone|iPod/.test(window.navigator.userAgent) && !window.MSStream
  }

  _isSafari() {
    const ua = window.navigator.userAgent
    const isSafari = /^((?!chrome|android).)*safari/i.test(ua)
    return isSafari
  }

  _isMac() {
    return /Macintosh|Mac OS X/.test(window.navigator.userAgent)
  }

  _showInstallInstructions() {
    // Keep it simple and platform-specific
    if (this._isIOS()) {
      alert(
        "To install this app on iPhone/iPad:\n\n" +
        "1) Tap the Share button in Safari\n" +
        '2) Choose "Add to Home Screen"\n' +
        "3) Confirm the name and tap Add"
      )
      return
    }

    if (this._isSafari() && this._isMac()) {
      // macOS Safari (Ventura+ / Sonoma+) supports Web Apps to Dock
      alert(
        "To install this app on macOS Safari:\n\n" +
        "1) Open Safari and go to this site\n" +
        '2) From the File menu, choose "Add to Dock…"\n' +
        "3) Confirm the name and click Add"
      )
      return
    }

    // Generic fallback (non-Chromium browsers that don't support beforeinstallprompt)
    alert(
      "App installation isn’t available via prompt on this browser.\n\n" +
      "Tip: Try using Chrome/Edge (Menu → Install App) or Safari’s Add to Home Screen / Add to Dock."
    )
  }

  _isDismissed() {
    try { return localStorage.getItem(this.storageKeyValue) === "1" } catch { return false }
  }
  _setDismissed(v) {
    try { localStorage.setItem(this.storageKeyValue, v ? "1" : "0") } catch {}
  }

  _showIfPresent(el) { if (el) el.classList.remove("hidden") }
  _hideIfPresent(el) { if (el) el.classList.add("hidden") }
  _removeIfPresent(el) { if (el && el.remove) el.remove() }
}
